import { useMemo, useState } from "react";
import { money, money2, count as fmt, pct1 } from "../lux";
import { humanMinutes } from "../PeriodPicker";
import { Button } from "../ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { ArrowUp, ArrowDown, DownloadSimple } from "@phosphor-icons/react";
import { SortTH } from "../financials/FinancialsTable";

// SALES-033 scope 6/7: the Salespeople table. A tab-specific component — the
// generic FinancialsTable is left untouched. Seven columns with two-line cells
// (six without money), five leader cards (four without money), a hover/focus
// explanation on every header, a plain-language key below, Export CSV with one
// column per figure, and a card layout with a Sort control below xl (1280).

const POINTS_START = "2026-10-01"; // from points.py (POINTS_START); read here, never changed

const trackedLabel = (iso) => {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

// A Lead close value {won, of, rate} | null -> the two lines it shows.
function closeLines(lc) {
  if (lc == null) return { main: "—", sub: "before tracking" };
  if (!lc.of) return { main: "—", sub: "no leads given" };
  return { main: pct1(lc.rate), sub: `${lc.won} of ${lc.of}` };
}

// Every column, in reference order. `money` columns/sublines drop for a
// no-money login. `sort` returns a comparable (nulls last in a desc sort).
function buildColumns(moneyOk, tracked, pointsBefore) {
  const cols = [
    {
      key: "salesperson", label: "Salesperson",
      explain: "The rep credited for the sale. Aliases and compound names are merged into one person.",
      align: "left", sort: (r) => (r.salesperson || "").toLowerCase(),
      cell: (r) => ({ main: r.salesperson, heading: true }),
    },
    {
      key: "cars_out", label: "Cars out", width: 116,
      explain: `Rentals that went out in this period — counted when the car went out, whenever the deal was sold.${moneyOk ? " Avg is revenue ÷ cars out." : ""}`,
      align: "right", sort: (r) => r.cars_out || 0,
      cell: (r) => ({ main: fmt(r.cars_out), sub: moneyOk ? `avg ${money(r.avg_booking_value)}` : null }),
    },
    moneyOk && {
      key: "revenue", label: "Revenue", money: true, width: 144,
      explain: "Revenue from cars that went out in this period. Net is after direct costs.",
      align: "right", sort: (r) => r.revenue || 0,
      cell: (r) => ({ main: money2(r.revenue), sub: `net ${money2(r.net_profit)}`, gold: true }),
    },
    {
      key: "bookings_made", label: "Bookings made", width: 164,
      explain: `Bookings the rep saved in this period (from ${tracked}), split into those that came from a lead and the rep's own business.`,
      align: "right", sort: (r) => (r.bookings_made == null ? -1 : r.bookings_made),
      cell: (r) => (r.bookings_made == null
        ? { main: "—", sub: `from ${tracked}` }
        : { main: fmt(r.bookings_made), sub: `${fmt(r.from_leads)} from leads · ${fmt(r.own)} own` }),
    },
    {
      key: "leads_given", label: "Leads given", width: 156,
      explain: "Leads assigned to the rep in this period, how many got a first reply, and the median time to that reply.",
      align: "right", sort: (r) => r.leads_given || 0,
      cell: (r) => ({
        main: fmt(r.leads_given),
        sub: (r.leads_given || r.leads_replied)
          ? `${fmt(r.leads_replied)} replied · ${humanMinutes(r.median_response_minutes)}` : null,
      }),
    },
    {
      key: "lead_close", label: "Lead close", width: 128,
      explain: `Of the leads given in this period (from ${tracked}), the share that became a booking. A dash means there were none to measure.`,
      align: "right", sort: (r) => (r.lead_close && r.lead_close.rate != null ? r.lead_close.rate : -1),
      cell: (r) => closeLines(r.lead_close),
    },
    {
      key: "points", label: "Points", width: 100,
      explain: "Sales points earned in this period. A dash means the period ends before points tracking began.",
      align: "right", sort: (r) => (pointsBefore ? -1 : (r.points || 0)),
      cell: (r) => ({ main: pointsBefore ? "—" : fmt(r.points) }),
    },
  ];
  return cols.filter(Boolean);
}

function downloadCsv(name, rows, moneyOk) {
  const head = ["Salesperson", "Cars out",
    ...(moneyOk ? ["Avg booking", "Revenue", "Net profit"] : []),
    "Bookings made", "From leads", "Own",
    "Leads given", "Leads replied", "Median response (min)",
    "Lead close %", "Lead close won", "Lead close of", "Points"];
  const v = (x) => (x == null ? "" : x);
  const line = (r) => [
    r.salesperson, r.cars_out,
    ...(moneyOk ? [r.avg_booking_value, r.revenue, r.net_profit] : []),
    v(r.bookings_made), v(r.from_leads), v(r.own),
    r.leads_given, r.leads_replied, v(r.median_response_minutes),
    r.lead_close ? v(r.lead_close.rate) : "", r.lead_close ? r.lead_close.won : "", r.lead_close ? r.lead_close.of : "",
    r.points,
  ].map((c) => {
    const s = String(v(c)).replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  }).join(",");
  const csv = [head.join(","), ...rows.map(line)].join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `${name}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

const TwoLine = ({ main, sub, gold, heading }) => (
  <div className="min-w-0">
    <div className={`tnum ${heading ? "text-sm font-medium truncate" : "text-sm"} ${gold ? "text-primary" : ""}`}>{main}</div>
    {sub && <div className="text-xs text-muted-foreground mt-0.5 truncate">{sub}</div>}
  </div>
);

export function SalespeopleTable({ rows = [], moneyOk, trackingStart, periodEnd, loading, onRowClick, exportName = "salespeople" }) {
  const tracked = trackedLabel(trackingStart) || "the start date";
  const pointsBefore = !!periodEnd && periodEnd < POINTS_START;
  const columns = useMemo(() => buildColumns(moneyOk, tracked, pointsBefore), [moneyOk, tracked, pointsBefore]);
  // msg_8 item 1: until the user picks a sort, the default FOLLOWS moneyOk live
  // (Revenue with money, Cars out without) — money_visible arrives after the
  // first render, so a key fixed in useState would strand admins on Cars out.
  // Once the user picks (sortKey set), their choice stays.
  const [sortKey, setSortKey] = useState(null);
  const [dir, setDir] = useState("desc");
  const defaultKey = moneyOk ? "revenue" : "cars_out";
  const activeKey = (sortKey && columns.some((c) => c.key === sortKey)) ? sortKey : defaultKey;

  const sorted = useMemo(() => {
    const col = columns.find((c) => c.key === activeKey) || columns[0];
    const arr = [...rows].sort((a, b) => {
      const av = col.sort(a), bv = col.sort(b);
      if (av < bv) return dir === "asc" ? -1 : 1;
      if (av > bv) return dir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [rows, columns, activeKey, dir]);

  const setSort = (key) => {
    if (key === activeKey) { setSortKey(key); setDir((d) => (d === "asc" ? "desc" : "asc")); }
    else { setSortKey(key); setDir(key === "salesperson" ? "asc" : "desc"); }
  };

  const sortable = columns; // msg_8 item 10: include Salesperson so a desktop sort is never blank after crossing xl
  const DirIcon = dir === "asc" ? ArrowUp : ArrowDown;

  const legend = (
    <p className="text-sm text-muted-foreground leading-relaxed" data-testid="salespeople-key">
      <strong className="text-foreground font-medium">Cars out</strong>{moneyOk ? <> and <strong className="text-foreground font-medium">Revenue</strong></> : null}: rentals that went out in this period, whenever they were sold.{" "}
      <strong className="text-foreground font-medium">Bookings made</strong>: bookings the rep saved in this period, split into those that came from a lead and the rep's own business.{" "}
      <strong className="text-foreground font-medium">Leads given</strong> and <strong className="text-foreground font-medium">Lead close</strong>: leads assigned to the rep in this period, and the share that became a booking.{" "}
      Tracked from {tracked}.
    </p>
  );

  return (
      <div className="space-y-4" data-testid="salespeople-table">
        <div className="flex items-center justify-between gap-3">
          <div className="label-mono" data-testid="salespeople-count">{loading ? "Loading…" : `${rows.length} reps`}</div>
          <Button variant="outline" size="sm" className="h-11 xl:h-9" onClick={() => downloadCsv(exportName, sorted, moneyOk)}
                  data-testid="salespeople-export" disabled={!rows.length}>
            <DownloadSimple size={15} className="mr-1.5" /> Export CSV
          </Button>
        </div>

        {/* Desktop table: >= xl (1280). */}
        <div className="hidden xl:block border border-border bg-card">
          <table className="w-full table-fixed">
            <colgroup>
              {columns.map((c) => (
                <col key={c.key} style={c.width ? { width: `${c.width}px` } : undefined} />
              ))}
            </colgroup>
            <thead>
              <tr className="border-b border-border">
                {columns.map((c) => (
                  <SortTH key={c.key} column={c} sort={{ key: activeKey, dir }} onSort={setSort} testid="sp" />
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {!loading && sorted.map((r) => (
                <tr key={r.salesperson} onClick={() => onRowClick?.(r)}
                    className="cursor-pointer hover:bg-white/[0.03] transition-colors"
                    data-testid={`sp-row-${r.salesperson}`}>
                  {columns.map((c) => (
                    <td key={c.key} className={`px-3 py-3 align-middle overflow-hidden ${c.align === "right" ? "text-right" : ""}`}>
                      <TwoLine {...c.cell(r)} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {!loading && !sorted.length && <div className="p-8 text-center text-sm text-muted-foreground">No bookings or leads in this period.</div>}
        </div>

        {/* Cards: below xl, with a Sort control. */}
        <div className="xl:hidden space-y-4">
          {rows.length > 1 && (
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <span className="label-mono shrink-0">Sort</span>
              <Select value={activeKey} onValueChange={(v) => setSort(v)}>
                <SelectTrigger className="w-full max-w-[180px] h-11" data-testid="sp-sort-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {sortable.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" size="icon" className="h-11 w-11 shrink-0" onClick={() => setDir((d) => (d === "asc" ? "desc" : "asc"))}
                    data-testid="sp-sort-dir" aria-label={`Sort ${dir === "asc" ? "ascending" : "descending"}`}>
              <DirIcon size={16} />
            </Button>
          </div>
          )}

          {!loading && sorted.map((r) => (
            <button key={r.salesperson} type="button" onClick={() => onRowClick?.(r)}
                    className="w-full text-left border border-border bg-card p-4 rounded-sm hover:bg-white/[0.03] transition-colors"
                    data-testid={`sp-card-${r.salesperson}`}>
              <div className="flex items-baseline justify-between gap-3 mb-3">
                <div className="font-heading text-xl truncate">{r.salesperson}</div>
                <div className="label-mono shrink-0">{pointsBefore ? "—" : `${fmt(r.points)} points`}</div>
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                {columns.filter((c) => c.key !== "salesperson" && c.key !== "points").map((c) => {
                  const cell = c.cell(r);
                  return (
                    <div key={c.key}>
                      <div className="label-mono mb-0.5">{c.label}</div>
                      <TwoLine {...cell} />
                    </div>
                  );
                })}
              </div>
            </button>
          ))}
          {!loading && !sorted.length && <div className="p-8 text-center text-sm text-muted-foreground">No bookings or leads in this period.</div>}
        </div>

        {legend}
      </div>
  );
}
